import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { AlertTriangle, CheckCircle2, CloudSun, Download, Drone, Info, MapPin, Scale, ShieldCheck, Wrench, XOctagon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api, ApiError } from "../../api/client";
import type { CoherenceFinding, CoherenceSeverity, MaterielFamily, Phase, RapforPhase, TacticalCoherence } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { EmptyState, Spinner } from "../../components/EmptyState";
import { Disclosure, DisclosureChevron, PanelGroup } from "../../components/panel";
import { Motion, Presence, useLastDefined } from "../../components/presence";
import { Badge, Button, FloatingPanel, textareaClass } from "../../components/ui";
import { VIEW_META } from "../../components/viewMeta";
import { Waiting } from "../../components/Waiting";
import { HelpButton } from "../../help/HelpButton";
import { formatNumber, lang, t } from "../../i18n/i18n";
import { unitMissionName } from "../../i18n/vocabulary";
import { useManoeuvreWrites } from "../../map/manoeuvre/useManoeuvreWrites";
import { useCompositionWrites } from "../../map/units/useCompositionWrites";
import { refreshOrders } from "../../map/orderSync";
import { type MapMask, unitMask } from "../../map/units/maneuverUnits";
import { effectiveOpo, useDeployment } from "../../map/units/useDeployment";
import { useUnitMoves } from "../../map/units/useUnitMoves";
import { plural } from "../../plural";
import { designateDossierReadOnly, useDossierLocked } from "../../sharing/access";
import { useUi } from "../../state/ui";
import { dronesSettingTarget, WEATHER_FIELD } from "../missionDataTarget";
import { designateDrones, OpenMissionDataButton, showMissionField } from "../openMissionData";
import { coherenceKey, coherenceText, useCoherence } from "./coherenceState";

/** Panneau « Cohérence tactique » (demande d'Alexis du 19/10/2026, docs/24) : constats du moteur classés Bloquant /
 *  À revoir / Information, chacun avec sa règle, l'élément désigné (carte centrée sur l'unité, rubrique
 *  de l'ordre ouverte) et, quand elle est sûre, sa correction en un clic ; acceptation avec une justification,
 *  mémorisée dans le dossier ; rapport de forces par temps. Ouvert d'office avant la validation d'un ordre (passer
 *  outre justifié) et avant le téléchargement du dossier complet (avertissement, jamais de blocage). */

export const COHERENCE_APPROVE = "coherence-approve";
export const COHERENCE_DOWNLOAD = "coherence-download";
const JUSTIFICATION_MIN = 10;

const SEVERITY: Record<CoherenceSeverity, { label: string; tone: "danger" | "warn" | "accent"; Icon: typeof Info; icon: string }> = {
  blocking: { label: "Bloquant", tone: "danger", Icon: XOctagon, icon: "text-danger" },
  review: { label: "À revoir", tone: "warn", Icon: AlertTriangle, icon: "text-warn" },
  info: { label: "Information", tone: "accent", Icon: Info, icon: "text-accent" },
};

/** Zoom de la carte sur une unité désignée, selon le niveau du dossier. */
const ZOOM: Record<string, number> = { COMPAGNIE: 14, REGIMENT: 12.5, CENTAC: 13, BRIGADE: 11.5, DIVISION: 10, CORPS: 8.5 };

/** Rapport de cohérence du dossier ouvert (relu dès que le dossier change de version ou qu'un ordre change). */
export function useCoherenceReport(enabled = true) {
  const exerciseId = useUi((s) => s.exerciseId);
  const params = useUi((s) => s.deploymentParams);
  const opo = effectiveOpo(params, useUi((s) => s.opo));
  const { data: deployment } = useDeployment();
  const version = deployment?.exercise_version ?? null;
  return useQuery({
    queryKey: coherenceKey(exerciseId, opo, version),
    queryFn: () => api.coherence(exerciseId as string, opo),
    enabled: enabled && Boolean(exerciseId) && Boolean(params?.exercise_id),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: false,
  });
}

/** Rapport « 1,5/1 » en français, « 1.5/1 » en anglais. */
function ratioLabel(fr: string, en: string | undefined): string {
  return lang() === "en" && en ? en : fr;
}

function severityLabel(severity: CoherenceSeverity): string {
  return severity === "blocking" ? t("Bloquant") : severity === "review" ? t("À revoir") : t("Information");
}

export function CoherencePanel() {
  const exerciseId = useUi((s) => s.exerciseId);
  const params = useUi((s) => s.deploymentParams);
  const opo = effectiveOpo(params, useUi((s) => s.opo));
  const query = useCoherenceReport();
  const report = query.data;
  const gate = useCoherence((s) => s.gate);
  const queryClient = useQueryClient();
  useGateDesignation(report);

  const setReport = (next: TacticalCoherence) => {
    queryClient.setQueriesData({ queryKey: ["coherence", exerciseId, opo] }, next);
  };

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={VIEW_META.coherence.icon}
      title={t("Cohérence tactique")}
      actions={<HelpButton screen="ordre" topic="ordre.coherence" buttonSize="sm" size={16} />}
      footer={gate && report && !report.incomplete ? <GateFooter report={report} /> : undefined}
    >
      {!exerciseId || !params?.exercise_id ? (
        <Motion origin="fade">
          <EmptyState variant="inline" action={<OpenMissionDataButton />}>
            <p className="text-ink-soft">
              {t(
                "La cohérence tactique contrôle la posture des deux camps, les missions selon le rôle de chaque unité, le placement, le rapport de forces, les mesures de coordination et la concordance des documents.",
              )}
            </p>
            <p className="mt-2">{t("Pour la contrôler, créez d'abord le dossier.")}</p>
          </EmptyState>
        </Motion>
      ) : query.isError ? (
        <div className="px-4 py-4">
          <Callout tone="danger">{query.error.message}</Callout>
        </div>
      ) : !report ? (
        <div className="flex items-center gap-2 px-4 py-6 text-ui text-ink-muted">
          <Spinner decorative /> <Waiting>{t("Contrôle de la cohérence…")}</Waiting>
        </div>
      ) : report.incomplete ? (
        <Motion origin="fade">
          <EmptyState variant="inline" action={<OpenMissionDataButton />}>
            <p>{t("Complétez les Données initiales pour contrôler la cohérence tactique.")}</p>
          </EmptyState>
        </Motion>
      ) : (
        <Motion origin="fade" className={clsx("transition-opacity", query.isFetching && "opacity-80")}>
          <Presence show={Boolean(gate)}>{gate && <GateNotice report={report} />}</Presence>
          <Summary report={report} />
          <RapforGroup report={report} />
          {(["blocking", "review", "info"] as const).map((severity) => {
            const items = report.findings.filter((f) => f.severity === severity && !f.accepted);
            return items.length ? (
              <FindingGroup key={severity} severity={severity} items={items} report={report} onReport={setReport} />
            ) : null;
          })}
          <AcceptedGroup report={report} onReport={setReport} />
          {!report.findings.some((f) => !f.accepted) && (
            <EmptyState compact icon={<CheckCircle2 size={24} className="text-ok" />} title={t("Dossier tactiquement cohérent")}>
              {t("Aucun point à revoir : posture, rôles, placement, rapport de forces et documents concordent.")}
            </EmptyState>
          )}
        </Motion>
      )}
    </FloatingPanel>
  );
}

/** Bandeau de l'action en attente : validation de l'ordre ou téléchargement du dossier complet. */
function GateNotice({ report }: { report: TacticalCoherence }) {
  const gate = useCoherence((s) => s.gate);
  if (!gate) return null;
  const blocking = report.counts.blocking;
  const review = report.counts.review;
  if (gate.kind === "download") {
    return (
      <div className="px-4 pt-3 pb-1">
        <Callout tone="warn" title={t("Avant de télécharger le dossier complet")}>
          {blocking
            ? t("{blocking} et {review} : corrigez ce qui doit l'être, puis téléchargez. Le téléchargement n'est jamais bloqué.", {
                blocking: plural(blocking, t("point bloquant"), t("points bloquants")),
                review: plural(review, t("point à revoir"), t("points à revoir")),
              })
            : t("{review}, aucun bloquant : corrigez ce qui doit l'être, puis téléchargez. Le téléchargement n'est jamais bloqué.", {
                review: plural(review, t("point à revoir"), t("points à revoir")),
              })}
        </Callout>
      </div>
    );
  }
  const name = gate.frago ? t("le FRAGO N°{n}", { n: gate.number }) : gate.oplan ? t("l'OPLAN") : t("l'OPO");
  const title = gate.frago
    ? t("Validation du FRAGO N°{n}", { n: gate.number })
    : gate.oplan
      ? t("Validation de l'OPLAN")
      : t("Validation de l'OPO");
  return (
    <div className="px-4 pt-3 pb-1">
      <Callout tone={blocking ? "danger" : "info"} title={title}>
        {blocking
          ? blocking === 1
            ? t("Un point bloquant empêche de valider {name} : corrigez-le, acceptez-le avec une justification ou passez outre.", { name })
            : t("{n} points bloquants empêchent de valider {name} : corrigez-les, acceptez-les avec une justification ou passez outre.", {
                n: blocking,
                name,
              })
          : t("Aucun point bloquant : {name} peut être validé. {review}.", {
              name,
              review: plural(review, t("point reste à revoir"), t("points restent à revoir")),
            })}
      </Callout>
    </div>
  );
}

/** Pied du panneau pendant une action en attente : valider (désigné quand plus rien ne bloque), passer outre avec
 *  une justification, ou télécharger quand même. */
function GateFooter({ report }: { report: TacticalCoherence }) {
  const gate = useCoherence((s) => s.gate);
  const clear = useCoherence((s) => s.clear);
  const confirm = useCoherence((s) => s.confirm);
  const setView = useUi((s) => s.setView);
  const [override, setOverride] = useState<string | null>(null);
  const overrideField = useRef<HTMLTextAreaElement>(null);
  if (!gate) return null;
  if (gate.kind === "download") {
    return (
      <div className="flex w-full flex-wrap items-center justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={clear}>
          {t("Corriger d'abord")}
        </Button>
        <Button
          size="sm"
          highlight
          data-attention={COHERENCE_DOWNLOAD}
          onClick={() => {
            const run = gate.run;
            clear();
            run();
          }}
        >
          <Download size={14} /> {t("Télécharger quand même")}
        </Button>
      </div>
    );
  }
  const blocking = report.counts.blocking;
  const back = () => setView(gate.oplan ? "oplan" : "order");
  const firstBlocking = () => designate('[data-coherence-severity="blocking"]');
  const approveLabel = gate.frago ? t("Valider le FRAGO") : gate.oplan ? t("Valider l'OPLAN") : t("Valider l'OPO");
  return (
    <div className="w-full space-y-2">
      <Presence show={override !== null}>
        {override !== null && (
          <Motion origin="fade" className="space-y-2">
            <label className="block">
              <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Justification du passer outre")}</span>
              <textarea
                ref={overrideField}
                className={textareaClass}
                rows={3}
                value={override}
                maxLength={1000}
                onChange={(e) => setOverride(e.target.value)}
                placeholder={t("Pourquoi valider malgré ces points ? (mémorisé dans le dossier)")}
              />
            </label>
          </Motion>
        )}
      </Presence>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            clear();
            back();
          }}
        >
          {t("Revenir à l'ordre")}
        </Button>
        {blocking > 0 && override === null && (
          <Button size="sm" variant="secondary" onClick={() => setOverride("")}>
            {t("Passer outre…")}
          </Button>
        )}
        {blocking > 0 && override !== null ? (
          <Button
            size="sm"
            variant="danger"
            blocked={override.trim().length < JUSTIFICATION_MIN}
            onBlocked={() => designate(overrideField.current, "field")}
            onClick={() => {
              confirm(gate.number, override.trim());
              back();
            }}
          >
            {t("Passer outre et valider")}
          </Button>
        ) : (
          <Button
            size="sm"
            data-attention={COHERENCE_APPROVE}
            highlight={blocking === 0}
            blocked={blocking > 0}
            onBlocked={firstBlocking}
            onClick={() => {
              confirm(gate.number);
              back();
            }}
          >
            <CheckCircle2 size={14} /> {approveLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Compteurs : bloquants, à revoir, informations, acceptés. */
function Summary({ report }: { report: TacticalCoherence }) {
  const c = report.counts;
  return (
    <PanelGroup flush>
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-1">
        <Badge tone={c.blocking ? "danger" : "neutral"} dot={c.blocking > 0}>
          {plural(c.blocking, t("bloquant"), t("bloquants"))}
        </Badge>
        <Badge tone={c.review ? "warn" : "neutral"}>{plural(c.review, t("à revoir"), t("à revoir"))}</Badge>
        <Badge tone="accent">{plural(c.info, t("information"), t("informations"))}</Badge>
        {c.accepted > 0 && <Badge>{plural(c.accepted, t("accepté"), t("acceptés"))}</Badge>}
      </div>
    </PanelGroup>
  );
}

/** Rapport de forces par temps : global, secteur par secteur dans le détail, avec la norme de la mission. */
function RapforGroup({ report }: { report: TacticalCoherence }) {
  const [open, setOpen] = useState(false);
  const phases = report.rapfor.phases.filter((p) => p.global.ratio !== null);
  if (!phases.length) return null;
  return (
    <PanelGroup
      title={t("Rapport de forces (RAPFOR)")}
      actions={
        <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} aria-expanded={open}>
          <DisclosureChevron open={open} /> {t("Détail")}
        </Button>
      }
    >
      <ul className="space-y-px">
        {phases.map((p) => (
          <li key={p.phase} className="flex min-h-8 items-center gap-2 px-2 py-1.5">
            <Scale size={14} className="shrink-0 text-ink-muted" aria-hidden />
            <span className="w-7 shrink-0 text-ui font-medium text-ink">{p.phase}</span>
            <span className="min-w-0 flex-1 text-ui text-ink">
              {ratioLabel(p.global.label, p.global.label_en)}
              {p.global.required !== null && (
                <span className="text-ink-muted"> · {t("norme {norm}", { norm: ratioLabel(p.global.required_label, p.global.required_label_en) })}</span>
              )}
            </span>
            <Badge size="sm" tone={p.global.verdict === "favourable" ? "ok" : p.global.verdict === "unfavourable" ? "warn" : "neutral"}>
              {p.global.verdict === "favourable" ? t("Favorable") : p.global.verdict === "unfavourable" ? t("Défavorable") : t("Sans norme")}
            </Badge>
          </li>
        ))}
      </ul>
      <Disclosure open={open}>
        <div className="space-y-2 px-4 pt-1 pb-2">
          {phases.map((p) => (
            <RapforDetail key={p.phase} phase={p} />
          ))}
          <p className="text-meta leading-snug text-ink-muted">
            {t(
              "Calcul en équivalents compagnie : section 1/3, bataillon ou régiment 3, brigade 9, division 27 ; artillerie 1/2, génie 1/4, soutien 0 ; potentiel saisi en multiplicateur. Secteurs : le fuseau partagé entre les unités de tête. Normes indicatives : attaquer 3/1, contre-attaquer 2/1, fixer et reconnaître 1/1, défense ferme 1/2, défendre, freiner, interdire 1/3.",
            )}
          </p>
          {capopsWeighted(report) && (
            <p className="text-meta leading-snug text-ink-muted" data-rapfor-capops>
              {t(
                "Pondéré par la CAPOPS : une unité amie dont la DTO ou les munitions chutent au-delà de la prévision (avaries et pertes définitives jouées en DIREX, hypothèses retouchées) pèse d'autant moins ; un dossier qui n'est pas dégradé pèse son potentiel.",
              )}
            </p>
          )}
        </div>
      </Disclosure>
    </PanelGroup>
  );
}

/** RAPFOR pondéré par la CAPOPS des unités amies (`rapfor.method.capops` du serveur, hors CENTAC). */
export function capopsWeighted(report: TacticalCoherence): boolean {
  const method = (report.rapfor as { method?: { capops?: boolean } }).method;
  return method?.capops === true;
}

function RapforDetail({ phase }: { phase: RapforPhase }) {
  return (
    <div>
      <div className="text-ui font-medium text-ink-soft">
        {t("{phase} : amis {ami} contre ennemis {eni}", {
          phase: phase.phase,
          ami: formatNumber(phase.global.ami, { maximumFractionDigits: 1 }),
          eni: formatNumber(phase.global.eni, { maximumFractionDigits: 1 }),
        })}
      </div>
      {phase.sectors.length > 0 && (
        <ul className="mt-0.5 space-y-0.5">
          {phase.sectors.map((s) => (
            <li key={s.head} className="flex items-center gap-2 text-meta text-ink-muted">
              <span className="min-w-0 flex-1 truncate">
                {t("Secteur de {unit} ({verb})", { unit: s.label, verb: unitMissionName(s.verb) })}
                {s.effort && ` · ${t("effort")}`}
              </span>
              <span className="tabular-nums">{ratioLabel(s.label_ratio, s.label_ratio_en)}</span>
              {s.required !== null && <span className="tabular-nums">/ {ratioLabel(s.required_label, s.required_label_en)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FindingGroup({
  severity,
  items,
  report,
  onReport,
}: {
  severity: CoherenceSeverity;
  items: CoherenceFinding[];
  report: TacticalCoherence;
  onReport: (r: TacticalCoherence) => void;
}) {
  return (
    <PanelGroup title={severityLabel(severity)} count={items.length}>
      <ul className="space-y-1">
        {items.map((f) => (
          <li key={f.id}>
            <FindingRow finding={f} report={report} onReport={onReport} />
          </li>
        ))}
      </ul>
    </PanelGroup>
  );
}

/** Calque qui cache l'unité désignée par un constat (unité d'appui sous la bascule « Appuis et soutien », camp
 *  masqué), null si elle est dessinée ou si le constat ne désigne pas une unité. La cohérence lit toutes les unités
 *  posées : une unité cachée reste contrôlée, le constat le dit et « Voir » la montre. */
export function useTargetMask(f: CoherenceFinding): MapMask | null {
  const { data: deployment } = useDeployment();
  const supportsVisible = useUi((s) => s.supportUnitsVisible);
  const sidesVisible = useUi((s) => s.unitsVisible);
  const target = f.target;
  if (target?.kind !== "unit") return null;
  const unit = deployment?.units?.find((u) => u.key === target.key) ?? deployment?.units?.find((u) => u.code === target.code);
  return unit ? unitMask(unit, supportsVisible, sidesVisible) : null;
}

/** Ce que « Voir » rouvre pour montrer une unité cachée. */
function maskNote(mask: MapMask): string {
  return mask === "supports"
    ? t("Unité d'appui ou de soutien cachée sur la carte (calque « Appuis et soutien ») : « Voir » l'affiche.")
    : t("Unités de ce camp cachées sur la carte : « Voir » les affiche.");
}

/** Désigne l'élément d'un constat : unité (carte centrée, unité sélectionnée, temps du constat ; cachée par un calque,
 *  le calque est rouvert), point ou ligne (carte centrée), paragraphe d'un ordre (rubrique ouverte dans l'éditeur),
 *  rapport de forces. */
export function useLocate(mask: MapMask | null = null) {
  const setSupportsVisible = useUi((s) => s.setSupportUnitsVisible);
  const setUnitsVisible = useUi((s) => s.setUnitsVisible);
  const notify = useUi((s) => s.notify);
  const select = useUi((s) => s.select);
  const flyTo = useUi((s) => s.requestFlyTo);
  const setPhase = useUi((s) => s.setPhase);
  const setView = useUi((s) => s.setView);
  const setOpo = useUi((s) => s.setOpo);
  const setFrago = useUi((s) => s.setFrago);
  const params = useUi((s) => s.deploymentParams);
  const openField = useCoherence((s) => s.openField);
  const zoom = ZOOM[params?.level ?? ""] ?? 11;
  return (f: CoherenceFinding): (() => void) | null => {
    const target = f.target;
    if (!target) return null;
    if (target.kind === "unit" && target.lngLat) {
      const lngLat = target.lngLat;
      return () => {
        if (mask === "side") {
          setUnitsVisible(target.side, true);
          notify({ text: t("Unités de ce camp affichées sur la carte"), tone: "ok" });
        } else if (mask === "supports") {
          setSupportsVisible(true);
          notify({ text: t("Appuis et soutien affichés sur la carte"), tone: "ok" });
        }
        if (target.phase) setPhase(target.phase);
        select({ layer: "unit", code: target.code, lngLat });
        flyTo(lngLat, zoom);
      };
    }
    if ((target.kind === "point" || target.kind === "line") && target.lngLat) {
      const lngLat = target.lngLat;
      return () => {
        if (target.kind === "point") setPhase(target.phase);
        flyTo(lngLat, zoom);
      };
    }
    if (target.kind === "field" && target.field) {
      const field = target.field;
      return () => {
        openField(target.order, field);
        setView(target.order === 0 ? "oplan" : "order");
      };
    }
    if (target.kind === "frago_field") {
      // FRAGO de la rotation CENTAC : sa vue s'ouvre à la place de l'OPO qu'il modifie.
      const frago = target.frago;
      return () => {
        setOpo(Math.floor(frago / 10));
        setFrago(frago);
        setView("order");
      };
    }
    // DTO sous la DTO requise : action dédiée « Ouvrir la maintenance » (`maintenanceFinding`).
    if (target.kind === "view" && (target.view === "maintenance" || target.view === "logistics")) return null;
    if (target.kind === "rapfor" || (target.kind === "view" && target.phase)) {
      const phase = target.phase;
      return phase ? () => setPhase(phase) : null;
    }
    return null;
  };
}

/** Constat que la météo de l'exercice explique (règles météo, écart avec la météo du chef) : action secondaire
 *  « Changer la météo », qui ouvre le réglage des Données initiales. Action de nuit par nuit sombre : seulement
 *  quand le ciel couvert l'assombrit (le serveur le dit dans `detail.weather`) ; la Lune n'en dépend pas, le
 *  correctif est dans l'ordre. */
export function weatherFinding(finding: Pick<CoherenceFinding, "rule" | "detail">): boolean {
  if (finding.rule === "weather.night_vision") return finding.detail?.weather === true;
  // Constat drone (chantier « Drones ») : seulement celui que le temps explique (vent sur les petits drones).
  if (finding.rule.startsWith("drones.")) return finding.detail?.weather === true;
  return finding.rule.startsWith("weather.") || finding.rule === "documents.chain_weather";
}

/** Constat de maintenance (chantier « DTO / CAPOPS ») : un parc de matériel passe sous sa DTO requise d'un temps ;
 *  action « Ouvrir la maintenance », qui ouvre la vue au premier temps concerné et y désigne le parc en cause. */
export function maintenanceFinding(finding: Pick<CoherenceFinding, "rule">): boolean {
  return finding.rule.startsWith("maintenance.");
}

/** Ouvre la vue Maintenance sur le parc en cause (`detail.family`), au temps où il passe sous sa DTO requise
 *  (`detail.phase`) : la vue désigne le parc (`focusMaintenance`). */
export function openMaintenance(finding: Pick<CoherenceFinding, "detail">) {
  const ui = useUi.getState();
  const phase = finding.detail?.phase as Phase | undefined;
  if (phase) ui.setPhase(phase);
  ui.focusMaintenance((finding.detail?.family as MaterielFamily | undefined) ?? null);
}

/** Constat drone (chantier « Drones ») : action secondaire « Régler l'emploi des drones », qui ouvre la rubrique
 *  « Drones » des Données initiales sur le réglage en cause. */
export function dronesFinding(finding: Pick<CoherenceFinding, "rule">): boolean {
  return finding.rule.startsWith("drones.");
}

/** Cible `data-attention` du réglage en cause dans la rubrique « Drones » (`detail.setting` du serveur : régime,
 *  bulle de brouillage, caméra thermique), ou undefined : la rubrique entière. */
export function droneSettingTarget(finding: Pick<CoherenceFinding, "detail">): string | undefined {
  return dronesSettingTarget(finding.detail?.setting);
}

/** Ouvre la rubrique « Drones » des Données initiales sur le réglage en cause (amendement A2) : la vue s'ouvre, puis
 *  la rubrique, une fois montée, ouvre ses réglages repliés et le réglage `target` est désigné (`designateDrones`).
 *  Sans `target` (menace drone de l'ENI), la rubrique entière est désignée. */
export function openDronesSettings(target?: string) {
  const ui = useUi.getState();
  ui.setView("mission");
  designateDrones(target);
  ui.notify({ text: t("Réglez l'emploi des drones."), tone: "info" });
}

function FindingRow({
  finding,
  report,
  onReport,
}: {
  finding: CoherenceFinding;
  report: TacticalCoherence;
  onReport: (r: TacticalCoherence) => void;
}) {
  const style = SEVERITY[finding.severity];
  const mask = useTargetMask(finding);
  const shownMask = useLastDefined(mask); // le texte reste lisible pendant sa sortie
  const locate = useLocate(mask)(finding);
  const [accepting, setAccepting] = useState<string | null>(null);
  const justification = useRef<HTMLTextAreaElement>(null);
  const locked = useDossierLocked();
  const exerciseId = useUi((s) => s.exerciseId);
  const notify = useUi((s) => s.notify);
  const fix = useFix();
  const accept = useMutation({
    mutationFn: (justification: string) => api.acceptFinding(exerciseId as string, finding.id, justification, report.opo),
    onSuccess: (next) => {
      onReport(next);
      setAccepting(null);
      notify({ text: t("Constat accepté : la justification est mémorisée dans le dossier."), tone: "ok" });
    },
    onError: (e: Error) => notify({ text: t("Acceptation non enregistrée"), detail: e.message, tone: "warn" }),
  });
  const short = (accepting ?? "").trim().length < JUSTIFICATION_MIN;
  const weather = weatherFinding(finding);
  const drones = dronesFinding(finding);
  const maintenance = maintenanceFinding(finding);
  const MaintenanceIcon = VIEW_META.maintenance.icon;
  return (
    <div
      className="rounded-lg px-2 py-1.5 transition-colors hover:bg-hover"
      data-coherence-severity={finding.severity}
      data-attention={`coherence-${finding.id}`}
    >
      <div className="flex gap-2.5">
        <span className={clsx("mt-0.5 flex shrink-0", style.icon)}>
          <style.Icon size={14} aria-label={severityLabel(finding.severity)} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-ui leading-snug font-medium break-words text-ink">{coherenceText(finding.title)}</div>
          <div className="mt-0.5 text-meta leading-snug break-words text-ink-soft">{coherenceText(finding.why)}</div>
          <Presence show={Boolean(mask && locate)}>
            {shownMask && (
              <Motion origin="fade">
                <div className="mt-0.5 text-meta leading-snug break-words text-ink-soft" data-coherence-masked={shownMask}>
                  {maskNote(shownMask)}
                </div>
              </Motion>
            )}
          </Presence>
        </div>
      </div>
      {(locate || finding.fix || finding.overridable || weather || drones || maintenance) && (
        <div className="flex flex-wrap items-center gap-1 py-2.5 pl-6">
          {locate && (
            <Button size="sm" variant="ghost" onClick={locate}>
              <MapPin size={14} /> {finding.target?.kind === "field" ? t("Ouvrir la rubrique") : t("Voir")}
            </Button>
          )}
          {finding.fix && (
            <Button
              size="sm"
              variant="secondary"
              disabled={fix.busy}
              blocked={locked}
              onBlocked={designateDossierReadOnly}
              onClick={() => fix.run(finding)}
            >
              <Wrench size={14} /> {coherenceText(finding.fix.label)}
            </Button>
          )}
          {maintenance && (
            <Button size="sm" variant="ghost" onClick={() => openMaintenance(finding)}>
              <MaintenanceIcon size={14} /> {t("Ouvrir la maintenance")}
            </Button>
          )}
          {drones && (
            <Button size="sm" variant="ghost" onClick={() => openDronesSettings(droneSettingTarget(finding))}>
              <Drone size={14} /> {t("Régler l'emploi des drones")}
            </Button>
          )}
          {weather && (
            <Button size="sm" variant="ghost" onClick={() => showMissionField(WEATHER_FIELD)}>
              <CloudSun size={14} /> {t("Changer la météo")}
            </Button>
          )}
          {finding.overridable && accepting === null && (
            <Button size="sm" variant="ghost" blocked={locked} onBlocked={designateDossierReadOnly} onClick={() => setAccepting("")}>
              <ShieldCheck size={14} /> {t("Accepter…")}
            </Button>
          )}
        </div>
      )}
      <Presence show={accepting !== null}>
        {accepting !== null && (
          <Motion origin="fade" className="space-y-2 pb-1 pl-6">
            <textarea
              ref={justification}
              className={textareaClass}
              rows={2}
              value={accepting}
              maxLength={1000}
              aria-label={t("Justification")}
              onChange={(e) => setAccepting(e.target.value)}
              placeholder={t("Justification (mémorisée dans le dossier)")}
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setAccepting(null)}>
                {t("Annuler")}
              </Button>
              <Button
                size="sm"
                disabled={accept.isPending}
                blocked={short}
                onBlocked={() => designate(justification.current, "field")}
                highlight={!short}
                onClick={() => accept.mutate((accepting ?? "").trim())}
              >
                {accept.isPending ? <Waiting>{t("Enregistrement…")}</Waiting> : t("Accepter")}
              </Button>
            </div>
          </Motion>
        )}
      </Presence>
    </div>
  );
}

/** Corrections en un clic : par les écritures de la carte (historique, « Annuler », OPO recomposé) ou la
 *  réinitialisation du paragraphe retouché ; le rapport suit (version du dossier, ordres relus). */
function useFix() {
  const { setMission } = useManoeuvreWrites();
  const { reset } = useUnitMoves();
  // Rôle choisi dans la composition du dossier (registre A126 : reconnaissance dans une défense).
  const { setRole } = useCompositionWrites();
  const exerciseId = useUi((s) => s.exerciseId);
  const notify = useUi((s) => s.notify);
  const queryClient = useQueryClient();
  const resetField = useMutation({
    mutationFn: (q: { number: number; field: string }) => api.resetOrderField(exerciseId as string, q.field, q.number),
    onSuccess: () => {
      refreshOrders(queryClient, exerciseId);
      notify({ text: t("Paragraphe repris du texte à jour"), tone: "ok" });
    },
    onError: (e: Error) => notify({ text: t("Correction non enregistrée"), detail: e instanceof ApiError ? e.message : String(e), tone: "warn" }),
  });
  return {
    busy: setMission.isPending || reset.isPending || resetField.isPending || setRole.isPending,
    run: (f: CoherenceFinding) => {
      const fix = f.fix;
      if (!fix) return;
      if (fix.kind === "mission") setMission.mutate(fix.body);
      else if (fix.kind === "role") setRole.mutate(fix.body);
      else if (fix.kind === "position") reset.mutate(fix.query);
      else resetField.mutate(fix.query);
    },
  };
}

function AcceptedGroup({ report, onReport }: { report: TacticalCoherence; onReport: (r: TacticalCoherence) => void }) {
  const [open, setOpen] = useState(false);
  const exerciseId = useUi((s) => s.exerciseId);
  const locked = useDossierLocked();
  const notify = useUi((s) => s.notify);
  const items = report.findings.filter((f) => f.accepted);
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeFinding(exerciseId as string, id, report.opo),
    onSuccess: (next) => {
      onReport(next);
      notify({ text: t("Acceptation retirée : le constat redevient ouvert."), tone: "info" });
    },
    onError: (e: Error) => notify({ text: t("Retrait non enregistré"), detail: e.message, tone: "warn" }),
  });
  if (!items.length) return null;
  return (
    <PanelGroup
      title={t("Acceptés")}
      count={items.length}
      actions={
        <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} aria-expanded={open}>
          <DisclosureChevron open={open} /> {open ? t("Masquer") : t("Afficher")}
        </Button>
      }
    >
      <Disclosure open={open}>
        <ul className="space-y-1">
          {items.map((f) => (
            <li key={f.id} className="rounded-lg px-2 py-1.5">
              <div className="text-ui leading-snug font-medium break-words text-ink-soft">{coherenceText(f.title)}</div>
              <div className="mt-0.5 text-meta leading-snug break-words text-ink-muted">
                {f.accepted?.kind === "override"
                  ? t("Passé outre à la validation : « {why} »", { why: f.accepted.justification })
                  : t("Accepté : « {why} »", { why: f.accepted?.justification ?? "" })}
                {f.accepted?.author ? ` — ${f.accepted.author}` : ""}
              </div>
              <div className="py-2.5">
                <Button size="sm" variant="ghost" blocked={locked} onBlocked={designateDossierReadOnly} onClick={() => revoke.mutate(f.id)}>
                  {t("Retirer l'acceptation")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Disclosure>
    </PanelGroup>
  );
}

/** Ouverture d'office du panneau (validation, téléchargement) : la première action attendue est désignée — le
 *  premier constat bloquant, sinon « Valider » ou « Télécharger quand même ». */
export function useGateDesignation(report: TacticalCoherence | undefined) {
  const gate = useCoherence((s) => s.gate);
  useEffect(() => {
    if (!gate || !report) return;
    const timer = window.setTimeout(() => {
      if (gate.kind === "approve" && report.counts.blocking > 0) designate('[data-coherence-severity="blocking"]');
      else designate(attentionSelector(gate.kind === "approve" ? COHERENCE_APPROVE : COHERENCE_DOWNLOAD));
    }, 900);
    return () => window.clearTimeout(timer);
  }, [gate, report]);
}

